import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const ProjectEditModal = ({ isOpen, onClose, project, onSave }) => {
  const [formData, setFormData] = useState({
    clientName: '',
    projectValue: '',
    startDate: '',
    deliveryDate: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (project) {
      setFormData({
        clientName: project?.clientName,
        projectValue: project?.projectValue?.toString(),
        startDate: format(new Date(project.startDate), 'yyyy-MM-dd'),
        deliveryDate: format(new Date(project.deliveryDate), 'yyyy-MM-dd')
      });
      setErrors({});
    }
  }, [project]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.clientName?.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (!formData?.projectValue || parseFloat(formData?.projectValue) <= 0) {
      newErrors.projectValue = 'Project value must be greater than 0';
    }

    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData?.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required';
    }

    if (formData?.startDate && formData?.deliveryDate) {
      if (new Date(formData.deliveryDate) <= new Date(formData.startDate)) {
        newErrors.deliveryDate = 'Delivery date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave({
        ...project,
        clientName: formData?.clientName?.trim(),
        projectValue: parseFloat(formData?.projectValue),
        startDate: formData?.startDate,
        deliveryDate: formData?.deliveryDate
      });
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (!project) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Project Details"
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
          >
            Save Changes
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Client Name"
          type="text"
          name="clientName"
          value={formData?.clientName}
          onChange={handleChange}
          error={errors?.clientName}
          placeholder="Enter client name"
          required
        />

        <Input
          label="Project Value"
          type="number"
          name="projectValue"
          value={formData?.projectValue}
          onChange={handleChange}
          error={errors?.projectValue}
          placeholder="Enter project value"
          min="0"
          step="0.01"
          required
        />

        <Input
          label="Start Date"
          type="date"
          name="startDate"
          value={formData?.startDate}
          onChange={handleChange}
          error={errors?.startDate}
          required
        />

        <Input
          label="Delivery Date"
          type="date"
          name="deliveryDate"
          value={formData?.deliveryDate}
          onChange={handleChange}
          error={errors?.deliveryDate}
          required
        />

        <div className="bg-muted rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="mb-2">Project status is automatically calculated based on delivery date:</p>
              <ul className="space-y-1 ml-4">
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-200"></span>
                  <span>On Track: More than 7 days remaining</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-200"></span>
                  <span>Due Soon: 7 days or less remaining</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-200"></span>
                  <span>Overdue: Past delivery date</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ProjectEditModal;